import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Grid,
  Chip,
  Button,
  Paper,
  Collapse,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAllPostMonthlyTargetsAction,
  getAllPostActivitiesAction,
  getAllPostMediaFilesAction,
} from '../../../store/actions';

const PostActivityDetailsView = ({ open, onClose, row }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));

  const [expandedMonthId, setExpandedMonthId] = useState(null);
  const [openImageDialog, setOpenImageDialog] = useState(false);

  const { successData: monthlyTargetsData, loading: monthlyTargetsLoading } = useSelector(
    (state) => state.getAllPostMonthlyTargetsReducer || {}
  );
  const { successData: activitiesData, loading: activitiesLoading } = useSelector(
    (state) => state.getAllPostActivityReducer || {}
  );
  const { successData: mediaFilesData, loading: mediaFilesLoading } = useSelector(
    (state) => state.getAllPostMediaFilesReducer || {}
  );

  useEffect(() => {
    if (open && row) {
      dispatch(getAllPostMonthlyTargetsAction());
      dispatch(getAllPostActivitiesAction());
    }
  }, [open, row, dispatch]);

  const filteredMonthlyTargets = useMemo(() => {
    if (!row || !monthlyTargetsData) return [];
    const monthlyTargetsList = monthlyTargetsData?.data || [];
    if (!Array.isArray(monthlyTargetsList)) return [];

    return monthlyTargetsList.filter((target) => {
      const targetPostTargetId = target.postTargetId ?? target.post_target_id;
      return String(targetPostTargetId) === String(row.id);
    });
  }, [row, monthlyTargetsData]);

  if (!row) return null;

  const getActivitiesForMonth = (monthId) => {
    let activitiesList = [];

    if (activitiesData?.activities) {
      activitiesList = activitiesData.activities;
    } else if (activitiesData?.data?.activities) {
      activitiesList = activitiesData.data.activities;
    } else if (Array.isArray(activitiesData?.data)) {
      activitiesList = activitiesData.data;
    } else if (Array.isArray(activitiesData)) {
      activitiesList = activitiesData;
    }

    if (!Array.isArray(activitiesList)) return [];

    return activitiesList.filter((activity) => {
      const activityPostTargetId = activity.post_target_id ?? activity.postTargetId;
      const activityPostMonthId = activity.post_month_id ?? activity.postMonthId;
      return String(activityPostTargetId) === String(row.id) && String(activityPostMonthId) === String(monthId);
    });
  };

  const handleOpenImages = (activityId) => {
    setOpenImageDialog(true);
    dispatch(
      getAllPostMediaFilesAction({
        endPoint: `activity-media/post?activityId=${activityId}`,
      })
    );
  };

  const handleCloseImages = () => {
    setOpenImageDialog(false);
  };

  const handleToggleActivities = (monthId) => {
    setExpandedMonthId(expandedMonthId === monthId ? null : monthId);
  };

  const getStatusColor = (isActive) => {
    if (isActive) return { backgroundColor: '#D1FAE5', color: '#047857' };
    return { backgroundColor: '#FECACA', color: '#B91C1C' };
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Post-Season Activity Details
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3, position: 'relative' }}>
            {isXs ? (
              <Box sx={{ p: 1, minWidth: 220, minHeight: 180, position: 'relative' }}>
                <Chip
                  label={(row?.isActive ?? row?.is_active) ? 'Active' : 'Inactive'}
                  size="small"
                  sx={{ position: 'absolute', top: 12, right: 12, ...getStatusColor(row?.isActive ?? row?.is_active), fontWeight: 600, fontSize: 12 }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    <span style={{ color: '#374151' }}>Season:</span> {row?.season?.name}
                    {row?.season?.year ? ` (${row.season.year})` : ''}
                  </Typography>
                  <Typography variant="body2">
                    <span style={{ color: '#374151' }}>Area Type:</span> {row?.target_area_type}
                  </Typography>
                  <Typography variant="body2">
                    <span style={{ color: '#374151' }}>Dealer visit:</span> {row?.dvDoneCount ?? row?.dv_done_count ?? 0}/{row?.dvCount ?? row?.dv_count ?? 0}
                  </Typography>
                  <Typography variant="body2">
                    <span style={{ color: '#374151' }}>Field Day:</span> {row?.fdDoneCount ?? row?.fd_done_count ?? 0}/{row?.fdCount ?? row?.fd_count ?? 0}
                  </Typography>
                  <Typography variant="body2">
                    <span style={{ color: '#374151' }}>Field Visit:</span> {row?.fvDoneCount ?? row?.fv_done_count ?? 0}/{row?.fvCount ?? row?.fv_count ?? 0}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Activity Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" color="textSecondary">Activity ID</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{row?.id}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" color="textSecondary">Season</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{row?.season?.name}{row?.season?.year ? ` (${row.season.year})` : ''}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" color="textSecondary">Target Area Type</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{row?.target_area_type}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" color="textSecondary">Dealer Visit</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{row?.dvDoneCount ?? row?.dv_done_count ?? 0}/{row?.dvCount ?? row?.dv_count ?? 0}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" color="textSecondary">Field Day</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{row?.fdDoneCount ?? row?.fd_done_count ?? 0}/{row?.fdCount ?? row?.fd_count ?? 0}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" color="textSecondary">Field Visit</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{row?.fvDoneCount ?? row?.fv_done_count ?? 0}/{row?.fvCount ?? row?.fv_count ?? 0}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" color="textSecondary">Status</Typography>
                    <Chip label={(row?.isActive ?? row?.is_active) ? 'Active' : 'Inactive'} size="small" sx={{ mt: 0.5, ...getStatusColor(row?.isActive ?? row?.is_active), fontWeight: 600 }} />
                  </Grid>
                </Grid>
              </>
            )}
          </Paper>

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Monthly Targets
          </Typography>

          {monthlyTargetsLoading ? (
            <Typography>Loading monthly targets...</Typography>
          ) : filteredMonthlyTargets.length > 0 ? (
            <Box>
              {filteredMonthlyTargets.map((target) => {
                const activities = getActivitiesForMonth(target.id);
                const isExpanded = expandedMonthId === target.id;
                return (
                  <Paper key={target.id} elevation={0} sx={{ mb: 2, borderRadius: 3, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
                    {isXs ? (
                      <Box sx={{ p: 1.5, minWidth: 200 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}><span style={{ color: '#374151' }}>Month ID:</span> {target.id}</Typography>
                          <Typography variant="body2"><span style={{ color: '#374151' }}>Month:</span> {target.monthYear ?? target.month_year}</Typography>
                          <Typography variant="body2"><span style={{ color: '#374151' }}>Dealer Visit:</span> {target.dvDoneCount ?? target.dv_done_count ?? 0}/{target.dvCount ?? target.dv_count ?? 0}</Typography>
                          <Typography variant="body2"><span style={{ color: '#374151' }}>Field Visit:</span> {target.fvDoneCount ?? target.fv_done_count ?? 0}/{target.fvCount ?? target.fv_count ?? 0}</Typography>
                          <Typography variant="body2"><span style={{ color: '#374151' }}>Field Day:</span> {target.fdDoneCount ?? target.fd_done_count ?? 0}/{target.fdCount ?? target.fd_count ?? 0}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center', mt: 1.5 }}>
                          {activities.length > 0 && (
                            <Button variant="outlined" color="primary" size="small" sx={{ borderRadius: 2, fontSize: 11, py: 0.3, px: 1 }} onClick={() => handleToggleActivities(target.id)}>
                              {isExpanded ? 'Hide' : 'View'} Activities
                            </Button>
                          )}
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 2, alignItems: 'center' }}>
                          <Box>
                            <Typography variant="caption" color="textSecondary">Month ID</Typography>
                            <Typography variant="body2" fontWeight={600}>{target.id}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="textSecondary">Month</Typography>
                            <Typography variant="body2" fontWeight={600}>{target.monthYear ?? target.month_year}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="textSecondary">Dealer Visit</Typography>
                            <Typography variant="body2" fontWeight={600}>{target.dvDoneCount ?? target.dv_done_count ?? 0}/{target.dvCount ?? target.dv_count ?? 0}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="textSecondary">Field Visit</Typography>
                            <Typography variant="body2" fontWeight={600}>{target.fvDoneCount ?? target.fv_done_count ?? 0}/{target.fvCount ?? target.fv_count ?? 0}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="textSecondary">Field Day</Typography>
                            <Typography variant="body2" fontWeight={600}>{target.fdDoneCount ?? target.fd_done_count ?? 0}/{target.fdCount ?? target.fd_count ?? 0}</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center' }}>
                          {activities.length > 0 && (
                            <Button variant="outlined" color="primary" size="small" sx={{ borderRadius: 2, fontSize: 11, py: 0.4, px: 1.5 }} onClick={() => handleToggleActivities(target.id)}>
                              {isExpanded ? 'Hide' : 'View'} Activities
                            </Button>
                          )}
                        </Box>
                      </Box>
                    )}

                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ px: 2, pb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#4B5563' }}>
                          Associated Activities
                        </Typography>

                        {activitiesLoading ? (
                          <Typography variant="body2" color="textSecondary" sx={{ py: 2, textAlign: 'center' }}>
                            Loading activities...
                          </Typography>
                        ) : activities.length > 0 ? (
                          isXs ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {activities.map((activity) => (
                                <Paper key={activity.id} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 1.5, position: 'relative', minWidth: 200, minHeight: 110 }}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}><span style={{ color: '#374151' }}>Activity ID:</span> {activity.id}</Typography>
                                    <Typography variant="body2"><span style={{ color: '#374151' }}>Village:</span> {activity?.village?.name ?? (activity.villageId ?? activity.village_id)}</Typography>
                                    <Typography variant="body2"><span style={{ color: '#374151' }}>Activity Name:</span> {activity.activityName ?? activity.activity_name}</Typography>
                                    <Typography variant="body2"><span style={{ color: '#374151' }}>Start Time:</span> {new Date(activity.startTime ?? activity.start_time).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</Typography>
                                    <Typography variant="body2"><span style={{ color: '#374151' }}>End Time:</span> {new Date(activity.endTime ?? activity.end_time).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</Typography>
                                    <Typography variant="body2"><span style={{ color: '#374151' }}>No. of Farmers:</span> {activity.noOfFarmers ?? activity.no_of_farmers ?? 0}</Typography>
                                  </Box>
                                  <Box sx={{ position: 'absolute', bottom: 8, right: 8, display: 'flex', gap: 1 }}>
                                    <IconButton color="primary" size="small" onClick={() => handleOpenImages(activity.id)}>
                                      <ImageIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Paper>
                              ))}
                            </Box>
                          ) : (
                            <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, overflow: 'auto' }}>
                              <Table size="small">
                                <TableHead sx={{ backgroundColor: '#F9FAFB' }}>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Activity ID</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Village</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Activity Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Start Time</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>End Time</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>No. of Farmers</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Actions</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {activities.map((activity) => (
                                    <TableRow key={activity.id}>
                                      <TableCell>{activity.id}</TableCell>
                                      <TableCell>{activity?.village?.name ?? (activity.villageId ?? activity.village_id)}</TableCell>
                                      <TableCell>{activity.activityName ?? activity.activity_name}</TableCell>
                                      <TableCell>{new Date(activity.startTime ?? activity.start_time).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</TableCell>
                                      <TableCell>{new Date(activity.endTime ?? activity.end_time).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</TableCell>
                                      <TableCell>{activity.noOfFarmers ?? activity.no_of_farmers ?? 0}</TableCell>
                                      <TableCell>
                                        <IconButton color="primary" size="small" onClick={() => handleOpenImages(activity.id)}>
                                          <ImageIcon fontSize="small" />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Paper>
                          )
                        ) : (
                          <Typography variant="body2" color="textSecondary" sx={{ py: 2, textAlign: 'center' }}>
                            No activities found.
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Paper>
                );
              })}
            </Box>
          ) : (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E5E7EB', textAlign: 'center' }}>
              <Typography color="textSecondary">No monthly targets found for this post-season activity.</Typography>
            </Paper>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={onClose} sx={{ borderRadius: 2, textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {openImageDialog && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            bgcolor: 'rgba(0,0,0,0.5)',
            zIndex: 2001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={handleCloseImages}
        >
          <Paper
            elevation={3}
            sx={{
              p: 3,
              borderRadius: 3,
              minWidth: 320,
              minHeight: 200,
              maxWidth: '90vw',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <IconButton onClick={handleCloseImages} sx={{ position: 'absolute', top: 8, right: 8 }}>
              <CloseIcon />
            </IconButton>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Activity Images
            </Typography>
            {mediaFilesLoading ? (
              <Typography>Loading images...</Typography>
            ) : (
              (() => {
                let mediaArr = [];
                if (Array.isArray(mediaFilesData?.media)) {
                  mediaArr = mediaFilesData.media;
                } else if (Array.isArray(mediaFilesData?.data?.media)) {
                  mediaArr = mediaFilesData.data.media;
                } else if (Array.isArray(mediaFilesData?.data)) {
                  mediaArr = mediaFilesData.data;
                }
                if (mediaArr.length > 0) {
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {mediaArr.map((file, idx) => (
                        <Box key={file.id || idx} sx={{ width: 120, height: 120, borderRadius: 2, overflow: 'hidden', border: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F9FAFB', cursor: 'pointer' }}
                          onClick={() => window.open(file.filePath || file.url || file.fileUrl || file.file_url, '_blank', 'noopener,noreferrer')}
                        >
                          <img
                            src={file.filePath || file.url || file.fileUrl || file.file_url}
                            alt={file.name || `Activity Image ${idx + 1}`}
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                          />
                        </Box>
                      ))}
                    </Box>
                  );
                } else {
                  return <Typography color="textSecondary">No images found for this activity.</Typography>;
                }
              })()
            )}
          </Paper>
        </Box>
      )}
    </>
  );
};

PostActivityDetailsView.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  row: PropTypes.object,
};

export default PostActivityDetailsView;
